import { useAuth } from '@client/hooks/useAuth';
import { useStoreMutation } from '@client/queries/useStoreMutation';
import { useStoreQuery } from '@client/queries/useStoreQuery';
import {
  createSubscription,
  deactivateSubscription,
  deleteSubscription,
  getAllSubscriptions,
  getSubscription,
  getUserSubscriptions,
  getActiveSubscriptions,
  updateSubscription,
  toggleSubscriptionStatus,
  getSubscriptionsByFeature,
} from '@client/services/subscription.service';

import type { TPaginateProps } from '@client/types/antd.type';
import type { IFeature, ISubscription } from '@common/types';
import type { TablePaginationConfig } from 'antd/es/table/interface';

// Query keys
export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  lists: () => [...subscriptionKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) =>
    [...subscriptionKeys.lists(), filters] as const,
  details: () => [...subscriptionKeys.all, 'detail'] as const,
  detail: (id: string) => [...subscriptionKeys.details(), id] as const,
  byFeature: (featureId: string) =>
    [...subscriptionKeys.all, 'byFeature', featureId] as const,
};

export const useSubscriptionQueries = () => {
  const { user } = useAuth();

  // Get all subscriptions
  const useAllSubscriptions = (
    params?: TPaginateProps & TablePaginationConfig,
  ) =>
    useStoreQuery({
      queryKey: subscriptionKeys.list({ type: 'all', ...params }),
      queryFn: () => getAllSubscriptions(params),
    });

  // Get user subscriptions
  const useUserSubscriptions = (
    params?: TPaginateProps & TablePaginationConfig,
  ) =>
    useStoreQuery({
      queryKey: subscriptionKeys.list({ type: 'user', ...params }),
      queryFn: () => getUserSubscriptions(params),
      enabled: !!user,
    });

  // Get active subscriptions
  const useActiveSubscriptions = (
    params?: TPaginateProps & TablePaginationConfig,
  ) =>
    useStoreQuery({
      queryKey: subscriptionKeys.list({ type: 'active', ...params }),
      queryFn: () => getActiveSubscriptions(params),
      enabled: !!user,
    });

  // Get subscriptions by type
  const useSubscriptionsList = (
    type: 'user' | 'active',
    params?: TPaginateProps & TablePaginationConfig,
  ) => {
    const queryFnMap = {
      user: getUserSubscriptions,
      active: getActiveSubscriptions,
    };

    return useStoreQuery({
      queryKey: subscriptionKeys.list({ type, ...params }),
      queryFn: () => queryFnMap[type](params),
      enabled: !!user,
    });
  };

  // Get subscription by id
  const useUserSubscription = (id: string) =>
    useStoreQuery({
      queryKey: subscriptionKeys.detail(id),
      queryFn: () => getSubscription(id),
      enabled: !!id,
    });

  // Create subscription mutation
  const useCreateSubscription = () =>
    useStoreMutation({
      mutationFn: ({
        data,
        features,
      }: {
        data: ISubscription;
        features: IFeature[];
      }) => createSubscription(data, features),
      invalidateQueries: [subscriptionKeys.lists()],
    });

  // Update subscription mutation
  const useUpdateSubscription = () =>
    useStoreMutation({
      mutationFn: ({
        id,
        data,
        features,
      }: {
        id: string;
        data: ISubscription;
        features: IFeature[];
      }) => updateSubscription(id, data, features),
      invalidateQueries: [subscriptionKeys.lists()],
    });

  // Delete subscription mutation
  const useDeleteSubscription = () =>
    useStoreMutation({
      mutationFn: (id: string) => deleteSubscription(id),
      invalidateQueries: [subscriptionKeys.lists()],
    });

  // Deactivate subscription mutation
  const useDeactivateSubscription = () =>
    useStoreMutation({
      mutationFn: (id: string) => deactivateSubscription(id),
      invalidateQueries: [subscriptionKeys.lists()],
    });

  // Toggle subscription status mutation
  const useToggleActive = () =>
    useStoreMutation({
      mutationFn: (data: ISubscription) => toggleSubscriptionStatus(data),
      invalidateQueries: [subscriptionKeys.lists()],
    });

  // Get subscriptions by feature
  const useSubscriptionsByFeature = (featureId: IFeature['id']) =>
    useStoreQuery({
      queryKey: subscriptionKeys.byFeature(featureId),
      queryFn: () => getSubscriptionsByFeature(featureId),
      enabled: !!featureId && !!user,
    });

  return {
    useAllSubscriptions,
    useUserSubscriptions,
    useActiveSubscriptions,
    useSubscriptionsList,
    useUserSubscription,
    useCreateSubscription,
    useUpdateSubscription,
    useDeleteSubscription,
    useDeactivateSubscription,
    useToggleActive,
    useSubscriptionsByFeature,
  };
};
